/**
 * Centralized theme configuration for the HCC Members application
 * All colors should be defined here for easy customization
 */

export interface AppColors {
  // Primary brand colors
  primary: string
  primaryLight: string
  primaryDark: string

  // Secondary colors
  secondary: string
  secondaryLight: string
  secondaryDark: string

  // Status colors
  success: string
  warning: string
  danger: string
  info: string

  // Background colors
  background: string
  backgroundSecondary: string
  backgroundTertiary: string

  // Text colors
  textPrimary: string
  textSecondary: string
  textMuted: string

  // Border colors
  border: string
  borderLight: string

  // Custom app-specific colors
  brand: string
  accent: string
  church: string
  sacrament: string
}

export const appColors: AppColors = {
  // Primary brand colors (HCC Blue)
  primary: '#43281C',
  primaryLight: '#4F75E3',
  primaryDark: '#0A2F95',

  // Secondary colors
  secondary: '#6C757D',
  secondaryLight: '#A8B0B8',
  secondaryDark: '#495057',

  // Status colors
  success: '#28A745',
  warning: '#FFC107',
  danger: '#DC3545',
  info: '#17A2B8',

  // Background colors
  background: '#FFFFFF',
  backgroundSecondary: '#F8F9FA',
  backgroundTertiary: '#E9ECEF',

  // Text colors
  textPrimary: '#212529',
  textSecondary: '#495057',
  textMuted: '#6C757D',

  // Border colors
  border: '#DEE2E6',
  borderLight: '#E9ECEF',

  // Custom app-specific colors
  brand: '#43281C', // Same as primary for consistency
  accent: '#FF6B6B',
  church: '#8B4513', // Brown color for church-related elements
  sacrament: '#9370DB', // Purple color for sacrament-related elements
}

export const darkModeColors: Partial<AppColors> = {
  // Background colors for dark mode
  background: '#1A1A1A',
  backgroundSecondary: '#2D2D2D',
  backgroundTertiary: '#404040',

  // Text colors for dark mode
  textPrimary: '#FFFFFF',
  textSecondary: '#E9ECEF',
  textMuted: '#ADB5BD',

  // Border colors for dark mode
  border: '#495057',
  borderLight: '#6C757D',

  // Brand color - lighter for better dark mode visibility
  brand: '#DDB967', // Lighter brown for dark mode
  accent: '#FF8A8A', // Lighter accent for dark mode
}

/**
 * Vuestic UI theme configuration
 */
export const vuesticThemeConfig = {
  colors: {
    variables: {
      // Map our colors to Vuestic's color system
      primary: appColors.primary,
      secondary: appColors.secondary,
      success: appColors.success,
      warning: appColors.warning,
      danger: appColors.danger,
      info: appColors.info,

      // Custom colors available in Vuestic components
      brand: appColors.brand,
      accent: appColors.accent,
      church: appColors.church,
      sacrament: appColors.sacrament,
    },
  },
}

/**
 * CSS custom properties for use in components
 */
export const cssVariables = {
  '--app-primary': appColors.primary,
  '--app-primary-light': appColors.primaryLight,
  '--app-primary-dark': appColors.primaryDark,
  '--app-secondary': appColors.secondary,
  '--app-success': appColors.success,
  '--app-warning': appColors.warning,
  '--app-danger': appColors.danger,
  '--app-info': appColors.info,
  '--app-background': appColors.background,
  '--app-background-secondary': appColors.backgroundSecondary,
  '--app-text-primary': appColors.textPrimary,
  '--app-text-secondary': appColors.textSecondary,
  '--app-border': appColors.border,
  '--app-brand': appColors.brand,
  '--app-accent': appColors.accent,
  '--app-church': appColors.church,
  '--app-sacrament': appColors.sacrament,
}
